import { useState } from 'react';
import clsx from 'clsx';
import Image from 'next/image';

// Types
import type { StaticImageData } from 'next/image';
import type { EveryMediaType, AlbumCardGridLayoutType } from '@/lib/albums/types';

//lib
import { rgbDataURL } from '@/utils/blurImage';

//components
import Icon from '@/components/base/Icon';

type MediaGridImageProps = {
    index: number;
    image?: string | StaticImageData;
    type: EveryMediaType;
    size: number;
    gridLayout: AlbumCardGridLayoutType;
};

const getFallBackIcon = (type: EveryMediaType) => {
    switch (type) {
        case 'photo':
        case 'image':
        case 'carousel':
            return 'content';
        case 'reel':
            return 'reel';
        case 'video':
            return 'video';
        case 'short':
            return 'story';
        case 'carousel-images':
            return 'carousel-images';
        case 'carousel-videos':
            return 'carousel-videos';
        default:
            return null;
    }
};

export function MediaGridImage({ index, image, type, size, gridLayout }: MediaGridImageProps) {
    const [error, setError] = useState<boolean>(false);

    if (error || !image) {
        return (
            <span
                data-testid={`media-grid-image-fallback-${type}`}
                className={clsx(
                    'flex h-full w-full items-center justify-center bg-icoGray-300 text-icoGray-500 dark:bg-icoDarkMode-deepBlack dark:text-icoDarkMode-wolf',
                    {
                        'rounded-[1px]': gridLayout === 'table'
                    }
                )}>
                <Icon
                    icon={getFallBackIcon(type)}
                    className={clsx({
                        'text-2xl':
                            (index === 0 || (index === 1 && size === 2)) && gridLayout === 'card',
                        'text-md': index !== 0 && size > 2 && gridLayout === 'card',
                        'text-xs':
                            (index === 0 || (index === 1 && size === 2)) && gridLayout === 'table',
                        'text-9': index !== 0 && size > 2 && gridLayout === 'table'
                    })}
                />
            </span>
        );
    }

    return (
        <Image
            key={index}
            src={image}
            placeholder="blur"
            blurDataURL={rgbDataURL(240, 242, 246)}
            fill
            className={clsx('h-full w-full object-cover', {
                'rounded-[1px]': gridLayout === 'table'
            })}
            sizes="(max-width: 350px) 100vw,
    max-width: 768px) 50vw,
    (max-width: 1200px) 33vw,
      25vw"
            onError={() => setError(true)}
            alt={`preview-image-${index}`}
        />
    );
}
